import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { useQuotation, useQuotationMutations, useQuotationVersions } from '@/hooks/use-quotations';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { QuoteVersionDiff } from './components/QuoteVersionDiff';
import { QuoteVersionTimeline } from './components/QuoteVersionTimeline';
import { QUOTATION_STATUSES } from './mock-data';
import type { QuotationStatus } from './types';

export function QuotationDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { quotation, isLoading, error, refetch } = useQuotation(id);
  const mutations = useQuotationMutations();
  const { versions: versionData, refetch: refetchVersions } = useQuotationVersions(id);

  // Dialog states
  const [showAcceptConfirm, setShowAcceptConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [showConvertConfirm, setShowConvertConfirm] = useState(false);
  const [showAdvanceDialog, setShowAdvanceDialog] = useState(false);
  const [showRefundConfirm, setShowRefundConfirm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [advanceAmount, setAdvanceAmount] = useState('');
  const [advanceMethod, setAdvanceMethod] = useState<'CASH' | 'CARD' | 'TRANSFER'>('CASH');
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // SALES-P2-3: Version diff selection state
  const [diffSelectedA, setDiffSelectedA] = useState<string | null>(null);
  const [diffSelectedB, setDiffSelectedB] = useState<string | null>(null);
  const [showVersionDiff, setShowVersionDiff] = useState(false);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('quotation.notFound')}
          </h1>
          <Button onClick={() => navigate('/quotations')} className="mt-4">
            {t('quotation.backToList')}
          </Button>
        </div>
      </div>
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('hu-HU');
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('hu-HU');
  };

  const getStatusBadge = (status: QuotationStatus) => {
    const config = QUOTATION_STATUSES.find(s => s.value === status);
    return (
      <span className={`rounded-full px-3 py-1 text-sm font-medium ${config?.color ?? ''}`}>
        {config ? t(config.labelKey) : status}
      </span>
    );
  };

  const handleSendEmail = () => {
    alert(
      t('quotation.sendEmailSubject', {
        email: quotation.partnerEmail ?? '',
        number: quotation.quotationNumber,
      })
    );
  };

  const handlePrintPDF = () => {
    alert(t('quotation.pdfGeneration', { number: quotation.quotationNumber }));
  };

  const handleSend = async () => {
    if (!id) return;
    setActionError(null);
    try {
      await mutations.sendQuotation(id);
      setActionSuccess(t('quotation.sentSuccess'));
      await refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('quotation.sendFailed'));
    }
  };

  const handleAccept = async () => {
    if (!id) return;
    setActionError(null);
    setShowAcceptConfirm(false);
    try {
      await mutations.acceptQuotation(id);
      setActionSuccess(t('quotation.acceptedSuccess'));
      await refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('quotation.acceptFailed'));
    }
  };

  const handleReject = async () => {
    if (!id) return;
    setActionError(null);
    setShowRejectConfirm(false);
    try {
      await mutations.rejectQuotation(id, rejectionReason || undefined);
      setRejectionReason('');
      setActionSuccess(t('quotation.rejectedSuccess'));
      await refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('quotation.rejectFailed'));
    }
  };

  const handleConvertToWorksheet = async () => {
    if (!id) return;
    setActionError(null);
    setShowConvertConfirm(false);
    try {
      const result = await mutations.convertToWorksheet(id);
      const wsId = result.data.worksheet.id;
      const wsNumber = result.data.worksheet.worksheetNumber;
      setActionSuccess(t('quotation.worksheetCreated', { number: wsNumber }));
      // Navigate to the new worksheet after a short delay
      setTimeout(() => navigate(`/worksheet/${wsId}`), 1500);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('quotation.conversionFailed'));
    }
  };

  const handleGoToWorksheet = () => {
    if (quotation.worksheetId) {
      navigate(`/worksheet/${quotation.worksheetId}`);
    }
  };

  const handleRecordAdvance = async () => {
    if (!id) return;
    setActionError(null);
    const amount = parseInt(advanceAmount, 10);
    if (!amount || amount <= 0) {
      setActionError(t('quote.advance.invalidAmount'));
      return;
    }
    if (amount > quotation.totalGross) {
      setActionError(t('quote.advance.exceedsTotal'));
      return;
    }
    setShowAdvanceDialog(false);
    try {
      await mutations.recordAdvancePayment(id, { amount, paymentMethod: advanceMethod });
      setActionSuccess(t('quote.advance.recorded'));
      setAdvanceAmount('');
      setAdvanceMethod('CASH');
      await refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('quote.advance.recordFailed'));
    }
  };

  const handleRefundAdvance = async () => {
    if (!id) return;
    setActionError(null);
    setShowRefundConfirm(false);
    try {
      await mutations.refundAdvancePayment(id);
      setActionSuccess(t('quote.advance.refunded'));
      await refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('quote.advance.refundFailed'));
    }
  };

  const handleCreateNewVersion = async () => {
    if (!id) return;
    setActionError(null);
    try {
      const result = await mutations.createNewVersion(id);
      const newId = result.data.id;
      setActionSuccess(t('quotation.newVersionCreated'));
      await refetchVersions();
      // Navigate to the new version after a short delay
      setTimeout(() => navigate(`/quotations/${newId}`), 1500);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('quotation.newVersionFailed'));
    }
  };

  // SALES-P2-3: Handle version selection for diff comparison
  const handleSelectVersionForDiff = (versionId: string) => {
    if (diffSelectedA === null) {
      // First selection
      setDiffSelectedA(versionId);
      setDiffSelectedB(null);
      setShowVersionDiff(false);
    } else if (diffSelectedA === versionId) {
      // Deselect A
      setDiffSelectedA(diffSelectedB);
      setDiffSelectedB(null);
      setShowVersionDiff(false);
    } else if (diffSelectedB === versionId) {
      // Deselect B
      setDiffSelectedB(null);
      setShowVersionDiff(false);
    } else if (diffSelectedB === null) {
      // Second selection -> auto-show diff
      setDiffSelectedB(versionId);
      setShowVersionDiff(true);
    } else {
      // Replace B with new selection
      setDiffSelectedB(versionId);
      setShowVersionDiff(true);
    }
  };

  const hasAdvance =
    quotation.advanceAmount && quotation.advancePaidAt && !quotation.advanceRefundedAt;
  const canPayAdvance =
    (quotation.status === 'sent' || quotation.status === 'accepted') && !quotation.advanceAmount;
  const canRefundAdvance = hasAdvance && !quotation.advanceDeductedFromInvoice;

  const canEdit = quotation.status === 'draft';
  const canSend = quotation.status === 'draft';
  const canAccept = quotation.status === 'sent';
  const canConvert = quotation.status === 'accepted' && !quotation.worksheetId;
  // SRV-FG-2-2: Can create new version from SENT, VIEWED, or REJECTED quotes
  const canCreateNewVersion =
    quotation.status === 'sent' ||
    (quotation.status as string) === 'viewed' ||
    quotation.status === 'rejected';
  const hasVersionHistory = (versionData?.totalVersions ?? 0) > 1;

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/quotations')}>
                {t('common.back')}
              </Button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {quotation.quotationNumber}
                  </h1>
                  {getStatusBadge(quotation.status)}
                  {/* SRV-FG-2-2: Version badge */}
                  {(quotation.version ?? 1) > 1 || hasVersionHistory ? (
                    <span className="rounded-full bg-indigo-100 px-3 py-1 text-sm font-medium text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300">
                      {t('quotation.versionOf', {
                        current: String(quotation.version ?? 1),
                        total: String(versionData?.totalVersions ?? quotation.totalVersions ?? 1),
                      })}
                    </span>
                  ) : null}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {quotation.partnerName}
                  {quotation.worksheetNumber &&
                    ` • ${t('quotation.worksheetLabel')} ${quotation.worksheetNumber}`}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handlePrintPDF}>
                PDF
              </Button>
              {quotation.partnerEmail && (
                <Button variant="outline" onClick={handleSendEmail}>
                  Email
                </Button>
              )}
              {canEdit && (
                <Button
                  className="bg-primary hover:bg-primary/90"
                  onClick={() => navigate(`/quotations/${id}/edit`)}
                >
                  {t('common.edit')}
                </Button>
              )}
              {canSend && (
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleSend}
                  disabled={mutations.isLoading}
                >
                  {t('quotation.sendButton')}
                </Button>
              )}
              {/* SRV-FG-2-2: Create New Version button */}
              {canCreateNewVersion && (
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  onClick={handleCreateNewVersion}
                  disabled={mutations.isLoading}
                >
                  {t('quotation.createNewVersion')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Partner info */}
            <Card>
              <CardHeader>
                <CardTitle>{t('quotation.partnerData')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('quotation.partnerName')}
                    </label>
                    <p className="text-gray-900 dark:text-gray-100">{quotation.partnerName}</p>
                  </div>
                  {quotation.partnerEmail && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('quotation.emailLabel')}
                      </label>
                      <p className="text-gray-900 dark:text-gray-100">
                        <a
                          href={`mailto:${quotation.partnerEmail}`}
                          className="text-blue-600 hover:underline dark:text-blue-400"
                        >
                          {quotation.partnerEmail}
                        </a>
                      </p>
                    </div>
                  )}
                  {quotation.partnerPhone && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('quotation.phone')}
                      </label>
                      <p className="text-gray-900 dark:text-gray-100">
                        <a
                          href={`tel:${quotation.partnerPhone}`}
                          className="text-blue-600 hover:underline dark:text-blue-400"
                        >
                          {quotation.partnerPhone}
                        </a>
                      </p>
                    </div>
                  )}
                  {quotation.worksheetNumber && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('quotation.relatedWorksheet')}
                      </label>
                      <p>
                        <button
                          onClick={handleConvertToWorksheet}
                          className="text-blue-600 hover:underline dark:text-blue-400"
                        >
                          {quotation.worksheetNumber} →
                        </button>
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Items */}
            <Card>
              <CardHeader>
                <CardTitle>{t('invoice.items')}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b bg-gray-50 dark:bg-slate-700/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                          {t('quotation.itemDescription')}
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                          {t('common.quantity')}
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                          {t('quotation.unitPrice')}
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                          {t('quotation.vat')}
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                          {t('quotation.totalColumn')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y dark:divide-slate-700">
                      {quotation.items.map(item => (
                        <tr key={item.id}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {item.description}
                            </p>
                            {item.productId && (
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {t('quotation.productId')} {item.productId}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">
                            {item.quantity} {item.unit}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">
                            {formatPrice(item.unitPriceNet)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300">
                            {item.vatRate}%
                          </td>
                          <td className="px-4 py-3 text-right">
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {formatPrice(item.totalGross)}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {t('quotation.netLabel')} {formatPrice(item.totalNet)}
                            </p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t-2 bg-gray-50 dark:bg-slate-700/50 dark:border-slate-600">
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-300"
                        >
                          {t('quotation.netTotal')}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-gray-100">
                          {formatPrice(quotation.subtotalNet)}
                        </td>
                      </tr>
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-2 text-right text-gray-600 dark:text-gray-300"
                        >
                          {t('quotation.vatTotal')}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-900 dark:text-gray-100">
                          {formatPrice(quotation.vatAmount)}
                        </td>
                      </tr>
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-3 text-right text-lg font-bold text-gray-900 dark:text-gray-100"
                        >
                          {t('quotation.grossTotal')}
                        </td>
                        <td className="px-4 py-3 text-right text-lg font-bold text-gray-900 dark:text-gray-100">
                          {formatPrice(quotation.totalGross)}
                        </td>
                      </tr>
                      {/* P-CSH-3: Advance deduction line */}
                      {hasAdvance && quotation.advanceDeductedFromInvoice && (
                        <tr className="border-t border-blue-200 dark:border-blue-800">
                          <td
                            colSpan={4}
                            className="px-4 py-2 text-right text-sm font-medium text-blue-600 dark:text-blue-400"
                          >
                            {t('quote.advance.deducted')}
                          </td>
                          <td className="px-4 py-2 text-right text-sm font-medium text-blue-600 dark:text-blue-400">
                            -{formatPrice(quotation.advanceAmount ?? 0)}
                          </td>
                        </tr>
                      )}
                      {hasAdvance && quotation.advanceDeductedFromInvoice && (
                        <tr>
                          <td
                            colSpan={4}
                            className="px-4 py-2 text-right text-lg font-bold text-green-700 dark:text-green-300"
                          >
                            {t('quotation.grossTotal')} ({t('quote.advance.deducted')})
                          </td>
                          <td className="px-4 py-2 text-right text-lg font-bold text-green-700 dark:text-green-300">
                            {formatPrice(quotation.totalGross - (quotation.advanceAmount ?? 0))}
                          </td>
                        </tr>
                      )}
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            {(quotation.notes || quotation.internalNotes) && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('common.notes')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {quotation.notes && (
                    <div className="mb-4">
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('quotation.customerNote')}
                      </label>
                      <p className="mt-1 text-gray-900 dark:text-gray-100">{quotation.notes}</p>
                    </div>
                  )}
                  {quotation.internalNotes && (
                    <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 dark:border-orange-800 dark:bg-orange-900/20">
                      <label className="text-sm font-medium text-orange-700 dark:text-orange-300">
                        {t('quotation.internalNote')}
                      </label>
                      <p className="mt-1 text-orange-800 dark:text-orange-200">
                        {quotation.internalNotes}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* SALES-P2-3: Version Timeline + Diff */}
            {hasVersionHistory && versionData && (
              <>
                <QuoteVersionTimeline
                  versions={versionData.versions}
                  currentVersionId={id ?? ''}
                  selectedIds={[diffSelectedA, diffSelectedB]}
                  onSelectVersion={handleSelectVersionForDiff}
                />
                {showVersionDiff && diffSelectedA && diffSelectedB && (
                  <QuoteVersionDiff
                    quoteIdA={diffSelectedA}
                    quoteIdB={diffSelectedB}
                    onClose={() => {
                      setShowVersionDiff(false);
                      setDiffSelectedA(null);
                      setDiffSelectedB(null);
                    }}
                  />
                )}
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status & dates */}
            <Card>
              <CardHeader>
                <CardTitle>{t('table.status')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('quotation.statusLabel')}
                    </span>
                    {getStatusBadge(quotation.status)}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('quotation.createdAtLabel')}
                    </span>
                    <span className="text-gray-900 dark:text-gray-100">
                      {formatDateTime(quotation.createdAt)}
                    </span>
                  </div>
                  {quotation.sentAt && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('quotation.sentAtLabel')}
                      </span>
                      <span className="text-gray-900 dark:text-gray-100">
                        {formatDateTime(quotation.sentAt)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('quotation.validUntilLabel')}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {formatDate(quotation.validUntil)}
                    </span>
                  </div>
                  {quotation.respondedAt && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('quotation.respondedAtLabel')}
                      </span>
                      <span className="text-gray-900 dark:text-gray-100">
                        {formatDateTime(quotation.respondedAt)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-4 dark:border-slate-700">
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('quotation.createdByLabel')}
                    </span>
                    <span className="text-gray-900 dark:text-gray-100">{quotation.createdBy}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Total */}
            <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-green-600 dark:text-green-300">
                    {t('quotation.quotationAmount')}
                  </p>
                  <p className="text-3xl font-bold text-green-700 dark:text-green-200">
                    {formatPrice(quotation.totalGross)}
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-300">
                    ({t('quotation.netLabel')} {formatPrice(quotation.subtotalNet)})
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Advance Payment Status (P-CSH-3) */}
            {hasAdvance && (
              <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20">
                <CardHeader>
                  <CardTitle className="text-blue-700 dark:text-blue-300">
                    {t('quote.advance.paid')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-blue-600 dark:text-blue-400">
                        {t('quote.advance.amount')}
                      </span>
                      <span className="font-bold text-blue-700 dark:text-blue-200">
                        {formatPrice(quotation.advanceAmount ?? 0)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-blue-600 dark:text-blue-400">
                        {t('quote.advance.method')}
                      </span>
                      <span className="text-blue-700 dark:text-blue-200">
                        {quotation.advancePaymentMethod === 'CASH'
                          ? t('quote.advance.methodCash')
                          : quotation.advancePaymentMethod === 'CARD'
                            ? t('quote.advance.methodCard')
                            : t('quote.advance.methodTransfer')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-blue-600 dark:text-blue-400">
                        {t('quote.advance.paidAt')}
                      </span>
                      <span className="text-blue-700 dark:text-blue-200">
                        {quotation.advancePaidAt && formatDateTime(quotation.advancePaidAt)}
                      </span>
                    </div>
                    {quotation.advanceDeductedFromInvoice && (
                      <div className="mt-2 rounded border border-green-300 bg-green-100 p-2 dark:border-green-700 dark:bg-green-900/30">
                        <p className="text-sm text-green-700 dark:text-green-300">
                          {t('quote.advance.deducted')}: {quotation.advanceDeductedFromInvoice}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
            {quotation.advanceRefundedAt && (
              <Card className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-900/20">
                <CardContent className="pt-4">
                  <p className="text-sm font-medium text-orange-700 dark:text-orange-300">
                    {t('quote.advance.refundedLabel')}
                  </p>
                  <p className="text-sm text-orange-600 dark:text-orange-400">
                    {formatDateTime(quotation.advanceRefundedAt)}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            <Card>
              <CardHeader>
                <CardTitle>{t('common.actions')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {/* Status messages */}
                  {actionError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                      <p className="text-sm text-red-700 dark:text-red-300">{actionError}</p>
                    </div>
                  )}
                  {actionSuccess && (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-900/20">
                      <p className="text-sm text-green-700 dark:text-green-300">{actionSuccess}</p>
                    </div>
                  )}

                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={handlePrintPDF}
                  >
                    {t('quotation.pdfDownload')}
                  </Button>
                  {quotation.partnerEmail && (
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={handleSendEmail}
                    >
                      {t('quotation.emailSend')}
                    </Button>
                  )}
                  {quotation.worksheetId && (
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={handleGoToWorksheet}
                    >
                      {t('quotation.openWorksheet')}
                    </Button>
                  )}

                  {/* SRV-FG-2-2: Create New Version in sidebar */}
                  {canCreateNewVersion && (
                    <>
                      <hr className="my-2 dark:border-slate-700" />
                      <Button
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                        onClick={handleCreateNewVersion}
                        disabled={mutations.isLoading}
                      >
                        {t('quotation.createNewVersion')}
                      </Button>
                    </>
                  )}

                  {/* Advance payment actions (P-CSH-3) */}
                  {canPayAdvance && (
                    <>
                      <hr className="my-2 dark:border-slate-700" />
                      <Button
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={() => setShowAdvanceDialog(true)}
                        disabled={mutations.isLoading}
                      >
                        {t('quote.advance.pay')}
                      </Button>
                    </>
                  )}
                  {canRefundAdvance && (
                    <Button
                      variant="outline"
                      className="w-full text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                      onClick={() => setShowRefundConfirm(true)}
                      disabled={mutations.isLoading}
                    >
                      {t('quote.advance.refund')}
                    </Button>
                  )}

                  {/* Accept/Reject for SENT quotes */}
                  {canAccept && (
                    <>
                      <hr className="my-2 dark:border-slate-700" />
                      <Button
                        className="w-full bg-green-600 hover:bg-green-700"
                        onClick={() => setShowAcceptConfirm(true)}
                        disabled={mutations.isLoading}
                      >
                        {t('quotation.recordAcceptance')}
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        onClick={() => setShowRejectConfirm(true)}
                        disabled={mutations.isLoading}
                      >
                        {t('quotation.recordRejection')}
                      </Button>
                    </>
                  )}

                  {/* Convert to worksheet for ACCEPTED quotes */}
                  {canConvert && (
                    <>
                      <hr className="my-2 dark:border-slate-700" />
                      <Button
                        className="w-full bg-primary hover:bg-primary/90"
                        onClick={() => setShowConvertConfirm(true)}
                        disabled={mutations.isLoading}
                      >
                        {t('quotation.generateWorksheet')}
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Accept Confirmation Dialog */}
      {showAcceptConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {t('quotation.acceptTitle')}
            </h3>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              {t('quotation.acceptConfirm', { number: quotation?.quotationNumber })}
            </p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('quotation.acceptAfterInfo')}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowAcceptConfirm(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={handleAccept}
                disabled={mutations.isLoading}
              >
                {mutations.isLoading ? t('quotation.processing') : t('quotation.accept')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirmation Dialog */}
      {showRejectConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {t('quotation.rejectTitle')}
            </h3>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              {t('quotation.rejectConfirm', { number: quotation?.quotationNumber })}
            </p>
            <div className="mt-4">
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('quotation.rejectionReasonLabel')}
              </label>
              <textarea
                value={rejectionReason}
                onChange={e => setRejectionReason(e.target.value)}
                className="w-full rounded-md border p-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100"
                rows={3}
                placeholder={t('quotation.rejectionPlaceholder')}
              />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectConfirm(false);
                  setRejectionReason('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleReject}
                disabled={mutations.isLoading}
              >
                {mutations.isLoading ? t('quotation.processing') : t('quotation.reject')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Advance Payment Dialog (P-CSH-3) */}
      {showAdvanceDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {t('quote.advance.pay')}
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {t('quote.advance.payDescription', { total: formatPrice(quotation.totalGross) })}
            </p>
            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('quote.advance.amount')} (Ft)
                </label>
                <input
                  type="number"
                  value={advanceAmount}
                  onChange={e => setAdvanceAmount(e.target.value)}
                  className="w-full rounded-md border p-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100"
                  placeholder="0"
                  min="1"
                  max={quotation.totalGross}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('quote.advance.method')}
                </label>
                <select
                  value={advanceMethod}
                  onChange={e => setAdvanceMethod(e.target.value as 'CASH' | 'CARD' | 'TRANSFER')}
                  className="w-full rounded-md border p-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100"
                >
                  <option value="CASH">{t('quote.advance.methodCash')}</option>
                  <option value="CARD">{t('quote.advance.methodCard')}</option>
                  <option value="TRANSFER">{t('quote.advance.methodTransfer')}</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAdvanceDialog(false);
                  setAdvanceAmount('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={handleRecordAdvance}
                disabled={mutations.isLoading}
              >
                {mutations.isLoading ? t('quotation.processing') : t('quote.advance.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Advance Confirmation Dialog (P-CSH-3) */}
      {showRefundConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {t('quote.advance.refund')}
            </h3>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              {t('quote.advance.refundConfirm', {
                amount: formatPrice(quotation.advanceAmount ?? 0),
              })}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowRefundConfirm(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={handleRefundAdvance}
                disabled={mutations.isLoading}
              >
                {mutations.isLoading ? t('quotation.processing') : t('quote.advance.refundBtn')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Convert to Worksheet Confirmation Dialog */}
      {showConvertConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {t('quotation.generateWorksheetTitle')}
            </h3>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              {t('quotation.generateWorksheetConfirm', { number: quotation?.quotationNumber })}
            </p>
            <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {t('quotation.generateWorksheetInfo')}
              </p>
              <ul className="mt-2 space-y-1 text-sm text-blue-600 dark:text-blue-400">
                <li>
                  {t('quotation.partnerLabel')} <strong>{quotation?.partnerName}</strong>
                </li>
                <li>
                  {t('quotation.itemCountLabel')}{' '}
                  <strong>
                    {quotation?.items.length} {t('common.piece')}
                  </strong>
                </li>
                <li>
                  {t('quotation.amountLabel')}{' '}
                  <strong>{quotation && formatPrice(quotation.totalGross)}</strong>
                </li>
              </ul>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowConvertConfirm(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                className="bg-primary hover:bg-primary/90"
                onClick={handleConvertToWorksheet}
                disabled={mutations.isLoading}
              >
                {mutations.isLoading ? t('quotation.generating') : t('quotation.generateWorksheet')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
